// src/components/common/CustomDataGridDesktop.tsx - WITH DEBUG LOGS

import React from 'react';
import { Datagrid, DatagridProps, RaRecord, useListContext } from 'react-admin';
import { Box, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from '@mui/material';

interface CustomDataGridDesktopProps<RecordType extends RaRecord = any> 
  extends DatagridProps<RecordType> {
  children: React.ReactNode;
  maxHeight?: string;
  stickyHeaders?: boolean;
}

const CustomDataGridDesktop = <RecordType extends RaRecord = any>({
  children,
  sx,
  // No fixed default cap (spec §6.4: no magic-px max-height). The table fills the
  // flex column and scrolls internally; pass `maxHeight` only for a screen that
  // deliberately wants a pixel cap.
  maxHeight,
  stickyHeaders = true,
  expand,
  rowClick,
  ...props
}: CustomDataGridDesktopProps<RecordType>) => {
  const theme = useTheme();
  const { isLoading, data } = useListContext();
  // Skeleton rows only for the FIRST fetch (no data yet) — a refetch behind
  // already-rendered rows should not flash the frame empty (F-B-008: unlike
  // CustomDataGridMobile, this component never checked isLoading at all).
  const showSkeleton = isLoading && !data?.length;
  const columnCount = React.Children.count(children) || 4;

  const effectiveRowClick = expand ? 'expand' : (rowClick !== undefined ? rowClick : false);

  // Dense table (v3): compact rows, canonical ink headers (typography from the
  // single source of truth custom.table.headCell), hairline dividers. The table
  // scrolls inside its own wrapper (Pattern A / spec §6.4) so the page never
  // grows a long scroll — the datagrid root is a flex column and
  // `.RaDatagrid-tableWrapper` is the single scroll region.
  const combinedSx = {
    // The datagrid root (this sx targets .RaDatagrid-root) fills the flex parent
    // as a column; its own border/radius (from the RaDatagrid theme override) is
    // the table's card frame.
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    minHeight: 0,

    '& .MuiTableCell-root': {
      padding: '8px 14px',
      borderBottom: `1px solid ${theme.custom.table.rowBorder}`,
      fontSize: '0.8125rem',
    },

    '& .MuiTableHead-root .MuiTableCell-root': {
      ...theme.custom.table.headCell,
      backgroundColor: theme.custom.table.headerBg,
      borderBottom: `1px solid ${theme.custom.table.headerBorder}`,
      padding: '10px 14px',

      ...(stickyHeaders && {
        position: 'sticky' as const,
        top: 0,
        zIndex: 10,
      }),
    },

    '& .MuiTableBody-root .MuiTableRow-root': {
      transition: 'background-color 0.12s ease',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },

      '&:last-child .MuiTableCell-root': {
        borderBottom: 'none',
      },
    },

    '& .RaDatagrid-expandedPanel': {
      backgroundColor: theme.palette.action.hover,
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2.5),
    },

    ...(expand && {
      '& .RaDatagrid-clickableRow': {
        cursor: 'pointer',
      },
    }),

    // The real scroll region (RA Datagrid v5 has no MuiTableContainer — it wraps
    // the table in .RaDatagrid-tableWrapper). Fill the remaining height and
    // scroll here, keeping the sticky header pinned and the pagination anchored
    // below. `maxHeight` is honoured as an optional cap when a caller passes one.
    '& .RaDatagrid-tableWrapper': {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      ...(maxHeight ? { maxHeight } : {}),
      scrollBehavior: 'smooth' as const,

      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.grey[300],
        borderRadius: '999px',
        '&:hover': {
          backgroundColor: theme.palette.grey[400],
        },
      },
    },

    [theme.breakpoints.down('md')]: {
      '& .MuiTableCell-root': {
        padding: theme.spacing(1, 1.25),
        fontSize: '0.8rem',
      },
    },

    ...sx,
  };

  return (
    <Box
      className="custom-datagrid-container"
      sx={{
        mt: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        minHeight: 0,
        // Without this a flex item is free to grow to its content width, and a wide
        // grid pushes past the window instead of scrolling inside its wrapper.
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box 
        sx={{ 
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {showSkeleton ? (
          // Frame + header stay, body rows are placeholders (hybrid-3c.md
          // "no layout jump when data lands") — same geometry the real
          // Datagrid renders (frame recipe reproduced here since the theme's
          // RaDatagrid override targets a class this plain <Table> lacks).
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.custom.table.headerBorder}`,
              borderRadius: theme.custom.radius.card,
              overflow: 'hidden',
            }}
          >
            <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  {Array.from({ length: columnCount }).map((_, i) => (
                    <TableCell key={i} sx={{ ...theme.custom.table.headCell, backgroundColor: theme.custom.table.headerBg, borderBottom: `1px solid ${theme.custom.table.headerBorder}` }}>
                      <Skeleton variant="text" width="60%" />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 6 }).map((_, row) => (
                  <TableRow key={row}>
                    {Array.from({ length: columnCount }).map((_, col) => (
                      <TableCell key={col} sx={{ borderBottom: `1px solid ${theme.custom.table.rowBorder}` }}>
                        <Skeleton variant="text" width={col === 0 ? '80%' : '50%'} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Datagrid
            {...props}
            expand={expand}
            rowClick={effectiveRowClick}
            bulkActionButtons={false}
            size="small"
            sx={combinedSx}
          >
            {children}
          </Datagrid>
        )}
      </Box>
    </Box>
  );
};

export default CustomDataGridDesktop;